import {
  DEFAULT_MODE,
  DEFAULT_PALETTE,
  readStoredMode,
  readStoredPalette,
  type Mode,
  type PaletteId,
} from "@/lib/theme";
import { resolveUnitPrefs, type UnitPrefs } from "@/lib/units";
import { GAS_UNITS, type GasUnit } from "@/types/gas-units";
import type { PricingUnit } from "@/types/generated/PricingUnit";
import type { Session } from "@/types/session";
import type {
  ChartColors,
  ChartLineWeight,
  PreferenceKey,
  UserPreferences,
  UserPreferencesPatch,
} from "@/types/users";

/**
 * A user's display preferences — the pure half (the hooks are
 * `useUserPreferences` / `useApplyPreferences`).
 *
 * Stored on the server (`session.preferences`, `PATCH /api/me/preferences`;
 * the catalog is `pilot_domain::users::preferences`). The preferences that
 * moved there from this browser's localStorage arrive as `null` until the
 * user chooses on the server; `resolvePreferences` fills each with what this
 * browser last showed, then the app's default, and resolves the eight unit
 * preferences into the `UnitPrefs` every rate and usage formats through
 * (`lib/units.ts`). `useAdoptBrowserPreferences` uploads those browser
 * choices once, so nobody's look or units reset when preferences moved.
 */

/** Every preference with a value — the user's, this browser's, or the default. */
export interface EffectivePreferences {
  theme_mode: Mode;
  theme_palette: PaletteId;
  chart_line_weight: ChartLineWeight;
  chart_colors: ChartColors;
  /** Units and precision for electric and gas rates and usage — format through `lib/units`. */
  units: UnitPrefs;
  show_maxwell_panel: boolean;
  show_guidance: boolean;
}

/** Choices made in this browser before preferences moved to the server. */
export interface BrowserPreferences {
  theme_mode?: Mode;
  theme_palette?: PaletteId;
  electric_rate_unit?: PricingUnit;
  gas_rate_unit?: GasUnit;
  gas_usage_unit?: GasUnit;
  show_maxwell_panel?: boolean;
  show_guidance?: boolean;
}

/**
 * The localStorage keys of the browser-only era that nothing reads once
 * adopted. They are not scoped to a person, so they are dropped after the
 * first user adopts them: a second user signing in on this browser must not
 * inherit them.
 */
export function legacyPreferenceKeys(userId: string): string[] {
  return ["mode", "palette", "app.pricing-unit", `app.gas-unit:${userId}`, "app.guidance-hidden"];
}

/**
 * Drops the browser-only era's `open` from `maxwell.surface`, keeping the
 * rail width that still lives there. Unscoped like the legacy keys above.
 */
export function dropLegacyMaxwellOpen(): void {
  try {
    const raw = localStorage.getItem("maxwell.surface");
    if (!raw) return;
    const { open, ...rest } = JSON.parse(raw) as { open?: unknown };
    if (open !== undefined) localStorage.setItem("maxwell.surface", JSON.stringify(rest));
  } catch {
    // a hand-edited value, or storage refused: nothing to keep
  }
}

const PRICING_UNIT_CODES: readonly PricingUnit[] = ["dollars_per_kwh", "cents_per_kwh", "dollars_per_mwh"];

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The choices this browser made before preferences moved to the server: its
 * look (the legacy `mode` / `palette` keys, not the `theme` cache of
 * whoever was last on screen) and any unit, guidance or Maxwell choice. Only
 * the browser-only era wrote these keys. A value outside the catalog reads as
 * absent.
 */
export function readBrowserPreferences(userId: string): BrowserPreferences {
  const prefs: BrowserPreferences = {};
  const mode = readStoredMode();
  if (mode) prefs.theme_mode = mode;
  const palette = readStoredPalette();
  if (palette) prefs.theme_palette = palette;

  const electric = storageGet("app.pricing-unit");
  if (PRICING_UNIT_CODES.includes(electric as PricingUnit)) prefs.electric_rate_unit = electric as PricingUnit;
  // The browser-only era had one gas unit, for rates and volumes alike.
  const gas = storageGet(`app.gas-unit:${userId}`);
  if (GAS_UNITS.includes(gas as GasUnit)) {
    prefs.gas_rate_unit = gas as GasUnit;
    prefs.gas_usage_unit = gas as GasUnit;
  }

  const guidanceHidden = storageGet("app.guidance-hidden");
  if (guidanceHidden === "true" || guidanceHidden === "false") {
    prefs.show_guidance = guidanceHidden !== "true";
  }
  const surface = storageGet("maxwell.surface");
  if (surface) {
    try {
      const open = (JSON.parse(surface) as { open?: unknown }).open;
      if (typeof open === "boolean") prefs.show_maxwell_panel = open;
    } catch {
      // a hand-edited value reads as absent
    }
  }
  return prefs;
}

/**
 * Every preference, filled in: the user's server-side choice, else what this
 * browser last showed (`browser`), else the app's default — light mode, the
 * default palette, the default units (`$/kWh`, kWh, `$/therm`, therms), each
 * at its unit's natural precision, Maxwell and guidance shown.
 */
export function resolvePreferences(
  session: Pick<Session, "preferences">,
  browser: BrowserPreferences = {},
): EffectivePreferences {
  const p = session.preferences;
  const units = resolveUnitPrefs({
    ...p,
    electric_rate_unit: p.electric_rate_unit ?? browser.electric_rate_unit ?? null,
    gas_rate_unit: p.gas_rate_unit ?? browser.gas_rate_unit ?? null,
    gas_usage_unit: p.gas_usage_unit ?? browser.gas_usage_unit ?? null,
  });
  return {
    theme_mode: p.theme_mode ?? browser.theme_mode ?? DEFAULT_MODE,
    theme_palette: p.theme_palette ?? browser.theme_palette ?? DEFAULT_PALETTE,
    chart_line_weight: p.chart_line_weight,
    chart_colors: p.chart_colors,
    units,
    show_maxwell_panel: p.show_maxwell_panel ?? browser.show_maxwell_panel ?? true,
    show_guidance: p.show_guidance ?? browser.show_guidance ?? true,
  };
}

/**
 * The browser choices to upload: each one the server has never been told.
 * A preference already chosen on the server is never overwritten by an old
 * browser's copy.
 */
export function browserPreferencesToAdopt(
  stored: UserPreferences,
  browser: BrowserPreferences,
): UserPreferencesPatch {
  const patch: UserPreferencesPatch = {};
  if (stored.theme_mode === null && browser.theme_mode) patch.theme_mode = browser.theme_mode;
  if (stored.theme_palette === null && browser.theme_palette) patch.theme_palette = browser.theme_palette;
  if (stored.electric_rate_unit === null && browser.electric_rate_unit) {
    patch.electric_rate_unit = browser.electric_rate_unit;
  }
  if (stored.gas_rate_unit === null && browser.gas_rate_unit) patch.gas_rate_unit = browser.gas_rate_unit;
  if (stored.gas_usage_unit === null && browser.gas_usage_unit) patch.gas_usage_unit = browser.gas_usage_unit;
  if (stored.show_maxwell_panel === null && browser.show_maxwell_panel !== undefined) {
    patch.show_maxwell_panel = browser.show_maxwell_panel;
  }
  if (stored.show_guidance === null && browser.show_guidance !== undefined) {
    patch.show_guidance = browser.show_guidance;
  }
  return patch;
}

/** What a cleared preference reads as — the server's own unset value. */
const CLEARED: { [K in PreferenceKey]: UserPreferences[K] } = {
  chart_line_weight: "standard",
  chart_colors: "default",
  theme_mode: null,
  theme_palette: null,
  electric_rate_unit: null,
  electric_rate_precision: null,
  electric_usage_unit: null,
  electric_usage_precision: null,
  gas_rate_unit: null,
  gas_rate_precision: null,
  gas_usage_unit: null,
  gas_usage_precision: null,
  show_maxwell_panel: null,
  show_guidance: null,
};

/**
 * `prefs` after `patch`, as the server will answer it — for showing a change
 * before the round trip (an optimistic session update).
 */
export function applyPreferencesPatch(
  prefs: UserPreferences,
  patch: UserPreferencesPatch,
): UserPreferences {
  const { clear = [], ...set } = patch;
  const next: Record<string, unknown> = { ...prefs };
  for (const [key, value] of Object.entries(set)) {
    if (value !== undefined) next[key] = value;
  }
  for (const key of clear) next[key] = CLEARED[key];
  return next as unknown as UserPreferences;
}
